import { api, withIdempotencyKey } from "@/lib/api/client";

import type { Appointment, AppointmentListResponse } from "./types";

interface ListParams {
  patient?: string;
  practitioner?: string;
  status?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
}

interface CreatePayload {
  patient: string;
  practitioner: string;
  scheduled_at: string;
  /** Optionnelle — par défaut 30 min côté backend si omise. */
  duration_minutes?: number;
  reason?: string;
}

/** Rendez-vous déjà pris par un praticien sur une journée (hors annulés/absents). */
export interface BusySlot {
  id: string;
  start: string;
  duration_minutes: number;
  status: string;
}

export const appointmentsApi = {
  /** Agenda occupé d'un praticien pour une date (YYYY-MM-DD) — sert au choix
   *  d'un créneau libre. */
  availability: async (practitioner: string, date: string): Promise<BusySlot[]> => {
    const { data } = await api.get<BusySlot[]>("/appointments/availability/", {
      params: { practitioner, date },
    });
    return data;
  },
  list: async (params: ListParams = {}): Promise<AppointmentListResponse> => {
    const { data } = await api.get<AppointmentListResponse>("/appointments/", { params });
    return data;
  },
  get: async (id: string): Promise<Appointment> => {
    const { data } = await api.get<Appointment>(`/appointments/${id}/`);
    return data;
  },
  create: async (payload: CreatePayload, idempotencyKey?: string): Promise<Appointment> => {
    const { data } = await api.post<Appointment>("/appointments/", payload, withIdempotencyKey(idempotencyKey));
    return data;
  },
  confirm: async (id: string): Promise<Appointment> => {
    const { data } = await api.post<Appointment>(`/appointments/${id}/confirm/`);
    return data;
  },
  start: async (id: string): Promise<Appointment> => {
    const { data } = await api.post<Appointment>(`/appointments/${id}/start/`);
    return data;
  },
  complete: async (id: string): Promise<Appointment> => {
    const { data } = await api.post<Appointment>(`/appointments/${id}/complete/`);
    return data;
  },
  cancel: async (id: string, reason: string): Promise<Appointment> => {
    const { data } = await api.post<Appointment>(`/appointments/${id}/cancel/`, { reason });
    return data;
  },
  noShow: async (id: string): Promise<Appointment> => {
    const { data } = await api.post<Appointment>(`/appointments/${id}/no-show/`);
    return data;
  },
  remind: async (id: string): Promise<Appointment> => {
    const { data } = await api.post<Appointment>(`/appointments/${id}/remind/`);
    return data;
  },
  /** Suppression douce. Distincte de `cancel` : un rendez-vous annulé existe
   *  toujours et continue de rattacher le patient — il empêche donc encore la
   *  suppression de sa fiche. Seule la suppression le détache vraiment. */
  remove: async (id: string): Promise<void> => {
    await api.delete(`/appointments/${id}/`);
  },
};
